import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CommunicationUserType,
  DriverSuggestionType,
  InputType,
  NotificationTypes,
  SuggestionType,
} from 'src/app/models/enums';
import {
  NotificationService,
  StorageService,
  UsersService,
  ClientsService,
  UtilsService,
} from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { ISuggestion } from 'src/app/models/suggestion';
import {
  getSuggestionForm,
  UserSuggestionData,
} from 'src/app/forms/suggestionForms';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { MessageReceiver } from 'src/app/models/enums/messageReceiver';

@Component({
  selector: 'app-suggestion-details',
  templateUrl: './suggestion-details.component.html',
  styleUrls: ['./suggestion-details.component.scss'],
  standalone: false,
})
export class SuggestionDetailsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() userType: CommunicationUserType;
  @Input() suggestion: any = {} as ISuggestion;
  @Input() newSuggestion: boolean;
  @Input() editSuggestion: boolean;
  @Input() shouldOpenConversation: boolean = false;
  @Output() closeDetails = new EventEmitter();
  @Output() suggestionEdited = new EventEmitter<ISuggestion>();

  @ViewChild('form') form: ElementRef;

  public originalSuggestion: any = {} as ISuggestion;
  public anonymousUser: boolean = false;

  public TooltipPosition = TooltipPosition;
  public InputType = InputType;
  public CommunicationUserType = CommunicationUserType;
  public images = images;

  public expanded = false;
  public edit = false;
  public shownSuggestionId?: number;

  public steps = [1];
  public currentStep: number = 1;
  public numberSteps: number = 3;

  public suggestionForm: any[];
  public suggestionFormControl: FormGroup;
  public invalid: any[] = [];

  public clients: any;
  public serviceInfos: any;

  public conversation: any[] = [];
  public conversationModal: Modal;
  public conversationModalShowed = false;

  public routerSubscription: Subscription;

  constructor(
    public literalService: LiteralService,
    public usersService: UsersService,
    public utilsService: UtilsService,
    public notificationService: NotificationService,
    public storageService: StorageService,
    private suggestionService: SuggestionService,
    private clientService: ClientsService,
    private serviceInfoService: ServiceInfoService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeModals();
      }
    });
  }

  ngAfterViewInit() {
    if (!this.newSuggestion && this.suggestion) this.shownSuggestionId = this.suggestion.id;
    this.initSuggestionForm();
  }

  async initSuggestionForm() {
    this.suggestion && await this.updateSuggestionForm(this.newSuggestion ? new UserSuggestionData({}) : undefined);
  }

  async ngOnChanges(changes?: SimpleChanges) {
    if (this.newSuggestion) {
      this.suggestionForm = [];
      this.shownSuggestionId = undefined;
    } else if (
      this.suggestion &&
      this.suggestion.id != this.shownSuggestionId
    ) {
      this.shownSuggestionId = this.suggestion.id;
      this.originalSuggestion = structuredClone(this.suggestion);
    } else if (!this.suggestion) {
      if (this.expanded) this.expanded = false;
    }

    if (changes && changes['editSuggestion'] && changes['editSuggestion'].currentValue) {
      this.edit = changes['editSuggestion'].currentValue;
      this.originalSuggestion = structuredClone(this.suggestion);
    }

    // Open conversation modal when shouldOpenConversation is true
    if (changes && changes['shouldOpenConversation']?.currentValue && this.suggestion && !this.newSuggestion) {
      setTimeout(() => this.openConversation(), 100);
    }

    if (changes && changes['editSuggestion'] && changes['editSuggestion'].currentValue) {
      this.edit = changes['editSuggestion'].currentValue;
      this.originalSuggestion = structuredClone(this.suggestion);
    }

    this.suggestion && this.updateSuggestionForm(this.newSuggestion ? new UserSuggestionData({}) : undefined);
    this.changeDetectorRef.detectChanges();
  }


  closeModals() {
    if (this.conversationModalShowed) this.conversationModal.hide();
  }

  getDetailIcon() {
    if (this.suggestion) {
      if (this.suggestion.type === DriverSuggestionType.OBJECT_FOUND) return images.suggestions.objectFound;
      if (this.suggestion.type === SuggestionType.OBJECT_LOST) return images.suggestions.objectLost;
      return images.suggestions[this.suggestion.type.toLowerCase() as keyof typeof images.suggestions];
    }
    return images.suggestions.complaint;
  }

  getDetailTitle() {
    if (this.newSuggestion) return 'new';
    if (this.suggestion) return this.suggestion.type.toLowerCase();
    return '';
  }

  setAnonymousUser() {
    this.anonymousUser = !this.anonymousUser;
    if (this.anonymousUser) {
      this.suggestion.userSuggester = {
        firstName: this.literalService.get('suggestions.anonymousUser', true),
        lastName: ''
      };
    } else {
      this.suggestion.userSuggester = null;
    }
    this.updateSuggestionForm();
  };

  async updateSuggestionForm(userSuggestionData?: UserSuggestionData, changes = [''], step?: number) {
    this.suggestionForm = await getSuggestionForm(this.suggestion, this.userType, this.literalService, this.usersService, this.storageService, this.clientService, userSuggestionData, changes, this.edit, this.newSuggestion, step ?? this.currentStep, this.serviceInfoService)
    this.numberSteps = Math.max(...this.suggestionForm.flatMap((element) => element.step || 0));
    this.getGridRows();
  }

  getGridRows() {
    let showingElements = this.suggestionForm.reduce((acc, curr) => acc + (curr.hidden ? 0 : 1), 0);
    if (!this.newSuggestion && showingElements > 5) showingElements = 6;
    let longTextsToCheck = this.suggestionForm.filter((data) => data.edit.inputType === InputType.LONG_TEXT);
    longTextsToCheck.forEach((data) => {
      if (!data.hidden && (this.newSuggestion || (this.edit && data.edit.editable) || data.value.length >= 100)) showingElements++;
    });
    
    const formContainer = document.getElementById('suggestionDetailForm');
    formContainer!.style.gridTemplateRows = this.newSuggestion
      ? `repeat(${showingElements}, auto)`
      : `repeat(${showingElements}, minmax(0, 1fr))`;
  }

  async changeData(data: any, element?: any) {
    if (data !== undefined && data !== '') {
      const valueToChange = element?.edit.valueToChange;

      switch (valueToChange) {
        case 'user':
          this.suggestion.userSuggester = data;
          if (this.anonymousUser) this.anonymousUser = false;
          //this.updateSuggestionForm(undefined, ['user']);
          break;
        case 'town':
          this.suggestion[valueToChange] = data;
          this.suggestion.service = undefined;
          this.updateSuggestionForm();
          break;
        case 'client':
        case 'service':
        case  'affair':
        case 'status':
        case 'priority':
          this.suggestion[valueToChange] = data;
          break;
        case 'type':
          this.suggestion[valueToChange] = data;
          this.updateSuggestionForm();
          break;
        default:
          this.suggestion[valueToChange] = data.value;
          break;
      }
    }
  }

  async scroll(event: Select2ScrollEvent) {
    if (event.way === 'down') {
      if (event.search !== '') {
        this.updateSuggestionForm(
          new UserSuggestionData({
            showMoreUsers: true,
            search: true,
            value: event.search,
          })
        );
      } else {
        this.updateSuggestionForm(new UserSuggestionData({ showMoreUsers: true, search: false }));
      }
    }
  };

  async search(event: Select2SearchEvent, valueToChange: string) {
    const value = event.search;
    if (valueToChange === 'user') {
      const dropDown = document.getElementsByClassName('select2-results__options')[0];
      this.updateSuggestionForm(
        new UserSuggestionData({
          showMoreUsers: false,
          search: true,
          value: event.search,
        })
      );
      dropDown.scroll({
        top: 0
      });
    }
  }

  continue() {
    if (!this.steps.includes(2) && this.currentStep === 1) {
      !this.steps.includes(2) && this.steps.push(2);
      this.currentStep = 2;
      this.updateSuggestionForm(new UserSuggestionData({ showMoreUsers: false }), ['client']);
    } else if (!this.steps.includes(3) && this.currentStep === 2) {
      !this.steps.includes(3) && this.steps.push(3);
      this.currentStep = 3;
      this.suggestion && this.updateSuggestionForm();
    }
    if (this.currentStep === this.numberSteps) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = document.querySelector('#suggestionsDetailContainer .scrollable-content');
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    });
  }

  async goBack() {
    await this.updateSuggestionForm(undefined, undefined, this.currentStep - 1);
    this.currentStep = this.currentStep - 1;
    this.steps.pop();
  }

  editAction(event: any) {
    event.stopPropagation();
    this.edit = true;
    this.originalSuggestion = structuredClone(this.suggestion);
    this.updateSuggestionForm();
  };

  public isValidDate(d: any) {
    return d instanceof Date && !isNaN(Number(d));
  }

  public close() {
    this.expanded = false;
    this.edit = false;
    this.newSuggestion = false;
    this.anonymousUser = false;
    this.currentStep = 1;
    this.numberSteps = 3;
    this.steps = [1];
    this.closeDetails.emit();
  }

  cancel(event: any) {
    event.stopPropagation();
    if (this.edit) {
      this.edit = false;
      this.suggestion = structuredClone(this.originalSuggestion);
      this.updateSuggestionForm();
    }
    else this.close();
  }


  public initFormControl() {
    if (this.newSuggestion) {
      if (this.numberSteps === 3 && this.currentStep === 1) {
        this.suggestionFormControl = new FormGroup({
          client: new FormControl(this.suggestion.client, [Validators.required])
        });
      } else if (this.currentStep === (this.numberSteps - 1)) {
        this.suggestionFormControl = new FormGroup({
          town: new FormControl(this.suggestion.town, [Validators.required]),
          userSuggester: new FormControl(this.suggestion.userSuggester, [Validators.required]),
        });
      } else {
        this.suggestionFormControl = new FormGroup({
          type: new FormControl(this.suggestion.type, [Validators.required]),
          affair: new FormControl(this.suggestion.affair, [Validators.required]),
          description: new FormControl(this.suggestion.description, [Validators.required]),
          priority: new FormControl(this.suggestion.priority, [Validators.required]),
          status: new FormControl(this.suggestion.status, [Validators.required]),
        });
      }
    } else {
      this.suggestionFormControl = new FormGroup({
        priority: new FormControl(this.suggestion.priority, [Validators.required]),
        status: new FormControl(this.suggestion.status, [Validators.required]),
        comments: new FormControl(this.suggestion.comments, [Validators.required])
      })
    }
  }

  public async findInvalidControls() {
    this.initFormControl();
    this.invalid = [];
    const controls = this.suggestionFormControl.controls;
    for (const name in controls) {
      if (controls[name].invalid && name != "comments") {
        this.invalid.push(name);
      }
    }
  }

  showError(value: string, field: string) {
    let errorText = '';
    this.invalid.map((invalidField: string) => {
      if (invalidField === field) {
        if (
          !this.suggestion[invalidField] ||
          this.suggestion[invalidField].length === 0
        ) {
          if (invalidField === 'email' || invalidField === 'phone')
            errorText = this.literalService.get('requiredFieldOrNext', true);
          else
            errorText = this.literalService.get('errors.requiredField', true);
        } else {
          errorText = this.literalService.get('errors.incorrectField', true);
        }
      }
    });
    return errorText;
  }

  resetSuggestionInfo() {
    this.edit = false;
    this.newSuggestion = false; 
    this.suggestionEdited.emit(this.suggestion);
    this.originalSuggestion = structuredClone(this.suggestion);
    this.ngOnChanges();
  }

  async onSubmit() {
    await this.findInvalidControls();
    if (this.invalid.length === 0) {
      try {
        if (this.edit) {
          this.suggestion = await this.suggestionService.updateSuggestion(this.suggestion);
        } else {
          this.suggestion = await this.suggestionService.createSuggestion(this.suggestion);
        }
        this.utilsService.showNotification(images.notifications.check, `suggestions.actions.${this.edit ? 'edit' : 'create'}`, NotificationTypes.SUCCESS);
        this.resetSuggestionInfo();
        this.close();
      } catch {
        this.utilsService.showNotification(images.notifications.warning, `suggestions.actions.${this.edit ? 'editError' : 'createError'}`, NotificationTypes.DANGER);
      }
    }
  }

  public isExpandedFormInvalid(invalidArray: string[]) {
    return (
      this.suggestionForm &&
      this.suggestionForm.some(
        (form) =>
          form.expanded &&
          form.edit &&
          form.edit.valueToChange &&
          invalidArray.includes(form.edit.valueToChange),
      )
    );
  }

  openConversation() {
    this.conversationModal = new Modal(document.getElementById('conversationModal'), {
      closable: false,
      onHide: () => (this.conversationModalShowed = false),
      onShow: () => (this.conversationModalShowed = true),
    });
    this.conversation = this.suggestion.conversation;
    this.conversationModal.show();
    this.shouldOpenConversation = false;
  }

  closeConversation() {
    this.conversation = [];
    this.conversationModal.hide();
  }

  sendMessage(newMessage: string) {
    this.suggestionService.sendConversationMessage(this.suggestion.id, newMessage).then((res: any) => {
      this.suggestion.conversation = structuredClone(res.conversation);
      this.conversation = structuredClone(res.conversation);
    })
  }

  async goToUserMessages() {
    const messageFilters = await this.storageService.getMessagesFilters();
    let newMessageFilters: any = structuredClone(JSON.parse(messageFilters));
    newMessageFilters = {
      ...newMessageFilters,
      receivers: {
        selectedReceiver: this.userType === CommunicationUserType.DRIVERS ? MessageReceiver.DRIVER : MessageReceiver.USER,
        selectedReceivers: [this.suggestion.userSuggester]
      }
    }
    this.storageService.setMessagesFilters(JSON.stringify(newMessageFilters));
    this.router.navigate([`/messages/${this.userType.toLowerCase()}`]);
  }

  disabledSaveButton() {
    if (this.edit && this.suggestion && this.originalSuggestion) return this.utilsService.compareObjects(this.suggestion, this.originalSuggestion).length === 0;
    if (this.suggestionForm) {
      this.initFormControl();
      return !this.suggestionFormControl.valid;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }
}
